import type { ApplyPatchOperation } from "~/protocol/patch";

// Wire types shared by server/ and web/ (spec: docs/project/web-workspace-spec.md).
// Pure TS and a leaf: nothing here imports outside protocol/, so the browser
// build compiles it without node types. The project row (db/tables.ts) derives
// from ProjectInfo, not the other way round.

export type { AgentEvent, UsageTotals } from "~/protocol/agent-event";
export * from "~/protocol/options";

import type { ProjectOptions } from "~/protocol/options";

export type { ApplyPatchOperation } from "~/protocol/patch";

/** Per-file caps on user uploads (PUT /files/:name): the server picks by the org's plan
 * (subscribed ⇒ pro, billing-spec decision 28); the client pre-checks the pro ceiling only. */
export const UPLOAD_LIMIT_BYTES = { free: 20 * 1024 * 1024, pro: 200 * 1024 * 1024 } as const;
export const uploadLimit = (subscribed: boolean): number =>
  subscribed ? UPLOAD_LIMIT_BYTES.pro : UPLOAD_LIMIT_BYTES.free;

const mb = (bytes: number) => `${bytes / 1024 / 1024} MB`;
/** What refusing a file over `limit` says — the server's 413 body and the client's own refusal
 * before any byte is sent (it knows the limit from `Snapshot.org`). On the free limit `upgrade`
 * is set: Pro would have taken the file, so the client offers it. */
export function uploadTooLarge(limit: number): { error: string; upgrade?: true } {
  if (limit < UPLOAD_LIMIT_BYTES.pro) {
    return {
      error: `This file is over the free upload limit of ${mb(limit)}. Pro takes files up to ${mb(UPLOAD_LIMIT_BYTES.pro)}.`,
      upgrade: true,
    };
  }
  return { error: `This file is over the upload limit of ${mb(limit)}.` };
}

export type FileKind = "text" | "image" | "video" | "audio" | "other";

/** The thumbnail beside a rendered video: the same path with a .jpg extension
 * (studio/thumbnail.ts cuts it; the top pane and the share page show it). */
export function thumbnailName(videoPath: string): string {
  return videoPath.replace(/\.[^./]+$/, ".jpg");
}

/** The slideshow GIF beside a rendered video: the same path with a .gif extension
 * (studio/gif.ts writes it from the moments studio/thumbnail.ts ranks). */
export function gifName(videoPath: string): string {
  return videoPath.replace(/\.[^./]+$/, ".gif");
}

export interface FileEntry {
  name: string;
  kind: FileKind;
  size: number;
  mtime: string; // ISO
  /** Opaque to the client; today Math.round(mtimeMs). Sent back as write_file.baseVersion. */
  version: number;
  /** A placeholder whose content is still being generated (background music). */
  pending?: true;
  /** Rendered videos only (an mp4 with its EDL script beside it): true = carries the
   * watermark, false = unlocked. Read from the link manifest at every stat, so a re-render
   * over the same name flips it back to true. Absent on uploads and recordings. */
  watermark?: boolean;
  share?: {
    id: string;
    link: string;
  };
}

export type AgentName = "director" | "explorer";

/** The one UI-level unit: stored in .director.<conversation>.json and streamed identically. */
export interface ChatLine {
  id: string; // uuid v7 — creation order, and the `messages` row id for message lines
  at: string; // ISO, the app clock (the row's timestamp is the DB's)
  agent: "user" | AgentName;
  body: ChatBody;
}

// No tool_result lines, no question/answer/notice kinds: a request_user_action
// prompt is an explorer message, the reply a user message, "no response in
// time" a message attributed to the agent it concerns.
export type ChatBody =
  | { kind: "message"; text: string }
  | { kind: "tool_call"; name: string; params: unknown }
  | { kind: "patch"; op: ApplyPatchOperation; output: string; ok: boolean };

/** What a suspended agent waits on: free text, or a sign-in for `domain` (answered by an
 * update_project that stores credentials for it — the secret never rides the chat). */
export type Ask =
  | { kind: "question"; prompt: string }
  | { kind: "credentials"; domain: string; prompt: string };

export interface AgentState {
  status: "idle" | "running" | "awaiting_user";
  /** explorer ⇒ the director is frozen inside its spawn_explorer_agent call. */
  active: AgentName;
  /** The pending ask while awaiting_user. */
  ask?: Ask;
  /** Why the last turn ended short, on the idle it left behind; gone at the next transition. */
  error?: string;
}

/** A video preset (studio/prompt-presets.ts): the dropdown label and the mission it stands for. */
export interface Preset {
  key: string;
  name: string;
  prompt: string;
}

/** The one text a stored-credentials answer is: the user's chat line and the explorer's tool output. */
export const credentialsSavedAnswer = (domain: string): string =>
  `Credentials for ${domain} are stored — retry the sign-in with @username and @password.`;

export interface Credential {
  username: string;
  password: string;
}

/** Per-domain credentials as stored (projects.credentials); suffix host matching. */
export type CredentialMap = Record<string, Credential>;

/** Usernames only — passwords never travel server → client. */
export type CredentialView = Record<string, { username: string }>;

/** The project record as clients see it (the list row and the workspace's project alike);
 * org, credentials and node pointers stay server-side. */
export interface ProjectInfo {
  id: string;
  name: string;
  targetUrl: string;
  createdAt: string; // ISO
  /** "Last modified": moves with the record and with the owning workspace's watcher. */
  updatedAt: string; // ISO
  /** `.mp4` count in the project dir, kept by the owning workspace's watcher. */
  videos: number;
  /** The row column the hosting node writes; a live client follows `agent.status` instead. */
  status: AgentState["status"];
  /** The audio settings (AUDIO tab), defaults filled in. */
  options: ProjectOptions;
}

export interface Share {
  id: string;
  projectId: string;
  userId: string;
  fileName: string;
  /** The video's presigned URL; empty while the share is pending. */
  videoLink: string;
  /** The pictures' presigned URLs, on the share list; the page itself uses `posterUrl`. */
  thumbnailLink?: string;
  gifLink?: string;
  private: boolean;
  /** `pending` = the video is still being made, the watch page shows the poster; `done` = the player. */
  status: "pending" | "done";
  /** The thumbnail at its stable URL on the watch host (`/v/<id>.jpg`). */
  posterUrl: string;
  createdAt: string; // ISO
  updatedAt: string; // ISO
}

/** The explorer's live browser view, when its host provides one (null locally). */
export interface BrowserInfo {
  liveUrl: string | null;
}

/** The viewer's org as the server sees it at connect (the project's org: routes are org-scoped). */
export interface OrgInfo {
  /** billing-spec's Definitions: a Stripe status of active or past_due; false with billing off. */
  subscribed: boolean;
}

/** One frame on connect. */
export interface Snapshot {
  project: ProjectInfo & { credentials: CredentialView };
  agent: AgentState;
  chat: ChatLine[];
  files: FileEntry[];
  browser: BrowserInfo | null;
  /** Added by the connection, not the workspace: the workspace knows no billing. */
  org: OrgInfo;
}

export type ResultError =
  | "busy"
  | "conflict"
  | "bad_name"
  | "unsupported"
  /** unlock_video: the Stripe keys are unset; the inputs moved since the render; no credit. */
  | "billing_off"
  | "stale_render"
  | "no_credits"
  /** Client-side: the socket closed before the result came back. */
  | "disconnected"
  | string;

/** Workspace → every client. */
export type WorkspaceEvent =
  | { kind: "snapshot"; snapshot: Snapshot }
  | { kind: "chat"; line: ChatLine }
  | { kind: "agent"; agent: AgentState }
  | { kind: "file"; entry: FileEntry }
  | { kind: "file_removed"; name: string }
  | { kind: "browser"; browser: BrowserInfo | null }
  | { kind: "project"; project: Snapshot["project"] }
  /** Push: show this file in a tab, in front — client-local tabs, so never in the snapshot. */
  | { kind: "open_tab"; name: string }
  /** One-shot estimate for a long task ("rendering"): the client fills a bar over `seconds`
   * on its own and drops it on any other event. Never in the snapshot. */
  | { kind: "progress"; task: string; seconds: number }
  /** The task waits for one of the node's slots — a render slot, or (`task` = `browser`) a cloud
   * browser; `ahead` = waiters in front of it (0 = next). Sent again as the line moves; for a
   * render the `progress` estimate follows once it starts, for a browser the explorer's
   * `browser_open`. Same life as `progress`: dropped on any other event, never in the snapshot. */
  | { kind: "queued"; task: string; ahead: number }
  /** The org's balance moved under an unlock here: one credit held as its build starts, given
   * back when the build fails. Never in the snapshot (GET /api/billing/credits reads it). */
  | { kind: "credits"; credits: number }
  | {
      kind: "result";
      id: number;
      ok: boolean;
      error?: ResultError;
      version?: number;
      payload?: ResultPayload;
    };

/** Who can do something about an org with no credits, by the clicker's role: an org admin of
 * a subscribed org, an org admin of one that isn't (Subscribe), or any other member. */
export type NoCredits = "admin" | "subscribe" | "member";

export type ResultPayload =
  | { kind: "share_project_file"; share: Share }
  /** An unlock that took or tried to take a credit: the org's balance after it. */
  | {
      kind: "unlock_video";
      credits: number;
      noCredits?: NoCredits;
      /** With `noCredits`: a top-up is on its way. */
      topUpPending?: true;
    };

/** A domain's credentials to set/replace, or null to remove them. */
export type CredentialUpdate = Record<string, { username: string; password: string } | null>;

/** Client → workspace; `id` is client-chosen and echoed in the `result`. */
export type WorkspaceCommand =
  | { kind: "chat"; id: number; text: string }
  | { kind: "cancel"; id: number }
  | { kind: "write_file"; id: number; name: string; baseVersion: number; content: string }
  | { kind: "share_project_file"; id: number; fileName: string }
  | {
      kind: "update_project";
      id: number;
      name?: string;
      targetUrl?: string;
      credentials?: CredentialUpdate;
      /** Merged into the stored options (`pronunciations` replaced whole); an unknown voice or
       * an out-of-range setting fails with `bad_option`. */
      options?: Partial<ProjectOptions>;
    }
  /** Generate a background music track from `prompt` into the project (AUDIO tab's Generate):
   * the placeholder appears as a pending `file` event at once, the real file replaces it. */
  | { kind: "generate_music"; id: number; prompt: string }
  /** Re-render the rendered video `fileName` without the watermark, in place, for one of the
   * org's credits (billing-spec decision 11). The result arrives when the file is overwritten
   * — about the video's own length — with a `credits` event and a `progress` event
   * ("unlocking") at the start (a `queued` one before `progress` while the node's render
   * slots are taken); a `file` event announces the new version, and the result's payload
   * carries the balance. Errors are sentences, or a code: `busy` while the director runs,
   * `billing_off`, `stale_render` (the inputs moved since the last render), `no_credits` (the
   * payload says who can fix it). */
  | { kind: "unlock_video"; id: number; fileName: string };

export type WorkspaceResult = Extract<WorkspaceEvent, { kind: "result" }>;

export type ShareViewCommand = { kind: "ping"; playbackPositionSeconds: number } | { kind: "play" };

export type ShareViewEventCommon = {
  userId?: string;
  userName?: string;
  userEmail?: string;
  orgId?: string;
  projectId?: string;
  timestamp: string;
  shareId: string;
  shareFileName: string;
  shareLink: string;
  shareSessionId?: string;
  ip?: string;
};

export type ShareViewEvent =
  | (ShareViewEventCommon & { kind: "played" })
  | (ShareViewEventCommon & {
      kind: "viewed";
      secondsWatched: number;
    });
